import React, { useEffect, useState } from "react";
import { Box, Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Chat, useChatContext } from "stream-chat-react";

import useStreamClient from "../../hooks/api/useStreamClient";
import ChannelSidebar from "./ChannelSidebar";
import ChatWindow from "./ChatWindow";
import CreateChannelModal from "./CreateChannelModal";

import "stream-chat-react/dist/css/index.css";
import "./Messages.css";

function MessagesLayout({ streamUser, isMobile }) {
  const { channel: activeChannel, setActiveChannel } = useChatContext();
  const [isViewingChannel, setIsViewingChannel] = useState(false);

  useEffect(() => {
    const currentCid = activeChannel?.cid || null;

    if (!isMobile) {
      setIsViewingChannel(true);
      return;
    }

    setIsViewingChannel(Boolean(currentCid));
  }, [activeChannel, isMobile]);

  return (
    <Box className="messages-layout" sx={{ width: "100%", height: "100%", minHeight: 0 }}>
      <Grid container sx={{ height: "100%" }}>
        <Grid
          size={{ xs: 12, sm: 4, md: 4, lg: 3 }}
          sx={{
            maxWidth: { sm: 360 },
            minWidth: 0,
            height: "100%",
            display: "flex",
            position: { xs: "absolute", sm: "relative" },
            inset: { xs: 0, sm: "auto" },
            zIndex: { xs: 2, sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            transform: { xs: isViewingChannel ? "translateX(-100%)" : "translateX(0)", sm: "none" },
            transition: { xs: "transform 220ms ease", sm: "none" },
            pointerEvents: { xs: isViewingChannel ? "none" : "auto", sm: "auto" },
          }}
        >
          <ChannelSidebar streamUser={streamUser} />
        </Grid>
        <Grid
          size="grow"
          sx={{
            minWidth: 0,
            minHeight: 0,
            height: "100%",
            display: "flex",
            position: { xs: "absolute", sm: "relative" },
            inset: { xs: 0, sm: "auto" },
            zIndex: { xs: 1, sm: "auto" },
            width: { xs: "100%", sm: "auto" },
            transform: { xs: isViewingChannel ? "translateX(0)" : "translateX(100%)", sm: "none" },
            transition: { xs: "transform 220ms ease", sm: "none" },
            pointerEvents: { xs: isViewingChannel ? "auto" : "none", sm: "auto" },
          }}
        >
          <ChatWindow
            isMobile={isMobile}
            onBack={() => {
              setIsViewingChannel(false);
              setActiveChannel(undefined);
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default function Messages({ createChannelTrigger = 0 }) {
  const { client, streamUser, isEmployee, loading, error } = useStreamClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (isEmployee && createChannelTrigger > 0) {
      setShowCreateModal(true);
    }
  }, [createChannelTrigger, isEmployee]);

  if (loading) {
    return <div style={{ padding: 16 }}>Loading messaging...</div>;
  }
  if (error) {
    return <div style={{ padding: 16, color: "#c62828" }}>{error}</div>;
  }
  if (!client) {
    return null;
  }

  return (
    <Chat client={client} theme={`str-chat__theme-${theme.palette.mode}`}>
      <MessagesLayout streamUser={streamUser} isMobile={isMobile} />
      {isEmployee && showCreateModal && (
        <CreateChannelModal onClose={() => setShowCreateModal(false)} />
      )}
    </Chat>
  );
}
